import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

import { safeTimeout } from 'common/helpers/timeoutHelper';

import { getConfig } from './configHelper';

export const TOKEN_LOCAL_STORAGE_KEY = 'cpanel_jwt_token';
export const TOKEN_COOKIE_NAME = 'token';

export function isTokenAvailable() {
  return !!getTokenFromLocalStorage();
}

export function getTokenFromLocalStorage() {
  return localStorage.getItem(TOKEN_LOCAL_STORAGE_KEY);
}

export function setTokenToLocalStorage(token: string) {
  return localStorage.setItem(TOKEN_LOCAL_STORAGE_KEY, token);
}

export function removeTokenFromLocalStorage() {
  return localStorage.removeItem(TOKEN_LOCAL_STORAGE_KEY);
}

/**
 * Checks whether a cookie is set with the JWT and updates the local storage with it if it exists.
 */
export function updateTokenFromCookie() {
  const token = Cookies.get(TOKEN_COOKIE_NAME);
  if (token) {
    setTokenToLocalStorage(token);
    Cookies.remove(TOKEN_COOKIE_NAME, { path: '/', domain: getConfig().authCookieDomain });
  }

  return !!token;
}

/**
 * Checks whether the token is set in the hash portion of the url and updates the local storage with it if it exists.
 */
export function updateTokenFromUrlHash() {
  const { hash } = document.location;
  const params = new URLSearchParams(hash ? `?${hash.substring(1)}` : '');
  const token = params.get('token');
  if (token) {
    setTokenToLocalStorage(token);
    params.delete('token');
    const rest = params.toString() ? `#${params.toString()}` : '';
    const formatted = rest.replace(/=&/g, '&').replace(/=$/g, '').replace(/=\?/g, '?');
    window.history.replaceState(
      {},
      document.title,
      document.location.pathname + document.location.search.toString() + formatted
    );
  }

  return !!token;
}

export function getTokenFromURLparam() {
  const urlParams = window.location.search.substr(1, window.location.search.length).split('&');
  const tokenParam = urlParams.find(urlParam => urlParam.indexOf('token=') === 0);
  const token = tokenParam ? tokenParam.split('=')[1] : undefined;
  return token;
}

/**
 * Checks whether the token is set in the search portion of the url and updates the local storage with it if it exists.
 */
export function updateTokenFromUrlParams() {
  const token = getTokenFromURLparam();
  if (token) {
    setTokenToLocalStorage(token);

    /**
     * If any search params exist alongside the token, we need to
     * keep them and filter out the token. If no other search params
     * are present, we remove the dangling '?' by passing an empty
     * string.
     */
    const params = new URLSearchParams(document.location.search);
    params.delete('token');
    let passedParams = '';
    if (params.toString().length > 0) {
      passedParams = `?${params.toString()}`;
    }
    window.history.replaceState({}, document.title, document.location.pathname + passedParams);
  }

  return !!token;
}

export function getTokenDuration(token: string) {
  const { exp, nbf } = jwtDecode<{ exp: number; nbf: number }>(token);
  return (exp - nbf) * 1000;
}

const TOKEN_EXCHANGE_THRESHOLD_INTERVAL = 45000;
const TOKEN_EXCHANGE_THRESHOLD_MIN_DIFF = 1000;
const TOKEN_EXCHANGE_THRESHOLD_MIN_DIFF_COUNT = 30;

export function getTokenExchangeTimeout(token: string) {
  const { exp } = jwtDecode<{ exp: number; nbf: number }>(token);
  const remainingDurationMs = exp * 1000 - Date.now().valueOf();

  // This is calculated in a convoluted way, mostly in order to make sure that the
  // token is exchanged before it expires minus some random number in order to handle
  // the expirations of tokens that expire at once (issued by a script).
  const timeout =
    remainingDurationMs -
    TOKEN_EXCHANGE_THRESHOLD_INTERVAL -
    Math.round(TOKEN_EXCHANGE_THRESHOLD_MIN_DIFF * Math.random() * TOKEN_EXCHANGE_THRESHOLD_MIN_DIFF_COUNT);

  return safeTimeout(timeout);
}
